import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FilmSlateIcon, TextTIcon, UserIcon, UsersIcon, ChatCircleIcon, BracketsRoundIcon, ArrowLineRightIcon, Image as ImageIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';

export type BlockType = 'scene-heading' | 'action' | 'character' | 'dialogue' | 'parenthetical' | 'transition' | 'shot' | 'dual-dialogue';

interface SlashMenuProps {
    position: { top: number; left: number };
    onSelect: (type: BlockType) => void;
    onClose: () => void;
    initialFilter?: string;
}

const ALL_ITEMS = [
    { type: 'scene-heading', label: 'Scene Heading', icon: FilmSlateIcon, desc: 'INT. / EXT. locations', keywords: ['scene', 'heading', 'int', 'ext', 'location', 'slug'] },
    { type: 'action', label: 'Action', icon: TextTIcon, desc: 'Description of action', keywords: ['action', 'description', 'narrative', 'text'] },
    { type: 'character', label: 'Character', icon: UserIcon, desc: 'Character name', keywords: ['character', 'name', 'who', 'speaker'] },
    { type: 'dialogue', label: 'Dialogue', icon: ChatCircleIcon, desc: 'What they say', keywords: ['dialogue', 'speech', 'talk', 'say', 'line'] },
    { type: 'parenthetical', label: 'Parenthetical', icon: BracketsRoundIcon, desc: '(voice or action)', keywords: ['parenthetical', 'wryly', 'beat', 'pause', 'direction'] },
    { type: 'transition', label: 'Transition', icon: ArrowLineRightIcon, desc: 'CUT TO: / FADE TO:', keywords: ['transition', 'cut', 'fade', 'dissolve', 'smash'] },
    { type: 'shot', label: 'Shot', icon: ImageIcon, desc: 'Specific camera angle', keywords: ['shot', 'camera', 'angle', 'close', 'wide', 'pov'] },
    { type: 'dual-dialogue', label: 'Dual Dialogue', icon: UsersIcon, desc: 'Two characters speaking simultaneously', keywords: ['dual', 'simultaneous', 'both', 'together', 'split'] },
];

export const SlashMenu: React.FC<SlashMenuProps> = ({ position, onSelect, onClose, initialFilter = '' }) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [filter, setFilter] = useState(initialFilter);
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Filter items based on search
    const filteredItems = filter.trim() === ''
        ? ALL_ITEMS
        : ALL_ITEMS.filter(item => {
            const query = filter.toLowerCase();
            return (
                item.label.toLowerCase().includes(query) ||
                item.type.toLowerCase().includes(query) ||
                item.keywords.some(kw => kw.includes(query))
            );
        });

    // Reset selection when filter changes
    useEffect(() => {
        setSelectedIndex(0);
    }, [filter]);

    // Focus input on mount
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    // Keyboard navigation
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (e.nativeEvent.isComposing) return;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev < filteredItems.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev =>
                    prev > 0 ? prev - 1 : filteredItems.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (filteredItems[selectedIndex]) {
                    onSelect(filteredItems[selectedIndex].type as BlockType);
                }
                break;
            case 'Escape':
                e.preventDefault();
                onClose();
                break;
            case 'Tab':
                e.preventDefault();
                if (filteredItems[selectedIndex]) {
                    onSelect(filteredItems[selectedIndex].type as BlockType);
                }
                break;
        }
    }, [filteredItems, selectedIndex, onSelect, onClose]);

    // Scroll selected item into view
    useEffect(() => {
        const selectedEl = menuRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
        selectedEl?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    return (
        <div
            ref={menuRef}
            role="dialog" aria-label="Choose screenplay element"
            className="fixed z-[9999] w-72 bg-zinc-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100"
            style={{ top: position.top, left: position.left }}
            onKeyDown={handleKeyDown}
        >
            {/* Search Input */}
            <div className="p-2 border-b border-white/5 bg-white/5">
                <div className="flex items-center gap-2 px-2 py-1 bg-white/5 rounded-lg border border-white/5 focus-within:border-indigo-500/50 transition-colors">
                    <MagnifyingGlassIcon className="w-3.5 h-3.5 text-zinc-500" />
                    <input
                        ref={inputRef}
                        type="text"
                        aria-label="Find screenplay element"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Find element..."
                        className="flex-1 bg-transparent border-none outline-none text-sm text-zinc-200 placeholder:text-zinc-600"
                    />
                    <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[9px] font-mono text-zinc-500 bg-zinc-800 rounded">
                        ↑↓
                    </kbd>
                </div>
            </div>

            {/* Items List */}
            <div className="p-1 max-h-72 overflow-y-auto">
                {filteredItems.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-zinc-500">
                        No blocks found
                    </div>
                ) : (
                    filteredItems.map((item, index) => (
                        <button
                            key={item.type}
                            data-index={index}
                            onClick={() => onSelect(item.type as BlockType)}
                            onMouseEnter={() => setSelectedIndex(index)}
                            className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors group text-left ${
                                index === selectedIndex
                                    ? 'bg-indigo-500/20 text-indigo-300'
                                    : 'text-zinc-400 hover:bg-white/5'
                            }`}
                        >
                            <div className={`p-2 rounded-md transition-colors ${
                                index === selectedIndex
                                    ? 'bg-indigo-500/20 text-indigo-400'
                                    : 'bg-zinc-800 group-hover:bg-zinc-700'
                            }`}>
                                <item.icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className={`text-sm font-medium truncate ${
                                    index === selectedIndex ? 'text-white' : 'text-zinc-200'
                                }`}>
                                    {item.label}
                                </div>
                                <div className={`text-[10px] truncate ${
                                    index === selectedIndex ? 'text-zinc-400' : 'text-zinc-500'
                                }`}>
                                    {item.desc}
                                </div>
                            </div>
                            {index === selectedIndex && (
                                <kbd className="px-1.5 py-0.5 text-[9px] font-mono text-zinc-500 bg-zinc-800 rounded">
                                    ↵
                                </kbd>
                            )}
                        </button>
                    ))
                )}
            </div>

            {/* Footer */}
            <div className="p-2 border-t border-white/5 bg-indigo-500/5 flex items-center justify-between">
                <div className="flex items-center gap-2 px-2 text-[10px] text-indigo-400/70">
                </div>
                <div className="flex items-center gap-1 text-[9px] text-zinc-600">
                    <kbd className="px-1 py-0.5 bg-zinc-800 rounded">esc</kbd>
                    <span>to close</span>
                </div>
            </div>
        </div>
    );
};
